import React, { useEffect, useRef, useState } from "react";
import { Eye } from "lucide-react";

interface VideoClipProps {
  clip: {
    id: number;
    videoSrc: string;
    title: string;
    posterSrc: string;
    views: string;
  };
  documentIsVisible: boolean;
  playbackRequest?: number;
  onPlaybackBlocked?: (clipId: number) => void;
  onPlaybackStarted?: (clipId: number) => void;
}

export const VideoClip: React.FC<VideoClipProps> = ({
  clip,
  documentIsVisible,
  playbackRequest = 0,
  onPlaybackBlocked,
  onPlaybackStarted,
}) => {
  const playerRef = useRef<HTMLVideoElement>(null);
  const [playerReady, setPlayerReady] = useState(false);

  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;
    if (!documentIsVisible) {
      player.pause();
      return;
    }

    let active = true;
    const handleBlockedPlayback = () => {
      if (!active) return;
      setPlayerReady(false);
      onPlaybackBlocked?.(clip.id);
    };

    try {
      void player.play().catch(handleBlockedPlayback);
    } catch {
      handleBlockedPlayback();
    }

    return () => {
      active = false;
    };
  }, [
    clip.id,
    documentIsVisible,
    onPlaybackBlocked,
    playbackRequest,
  ]);

  const handlePlaying = () => {
    setPlayerReady(true);
    onPlaybackStarted?.(clip.id);
  };

  const handlePlaybackError = () => {
    setPlayerReady(false);
    onPlaybackBlocked?.(clip.id);
  };

  return (
    <article
      className="group w-64 flex-none snap-center sm:w-72"
      aria-label={clip.title}
    >
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
        <div className="relative aspect-[9/16] overflow-hidden bg-gray-100">
          <img
            src={clip.posterSrc}
            alt=""
            aria-hidden="true"
            width={640}
            height={1138}
            loading="eager"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <video
            ref={playerRef}
            data-proof-video
            src={clip.videoSrc}
            poster={clip.posterSrc}
            muted
            loop
            autoPlay
            controls={false}
            playsInline
            preload="auto"
            onLoadStart={() => setPlayerReady(false)}
            onPlaying={handlePlaying}
            onError={handlePlaybackError}
            aria-hidden="true"
            tabIndex={-1}
            className={`tc-proof-video absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ${
              playerReady ? "opacity-100" : "opacity-0"
            }`}
          />

          <div className="pointer-events-none absolute bottom-4 left-4 right-4">
            <div className="flex items-center justify-between text-white">
              <div className="flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 backdrop-blur-sm">
                <Eye aria-hidden="true" className="h-3 w-3" />
                <span className="text-xs font-medium">{clip.views}</span>
              </div>
              <div className="rounded-full bg-green-500 px-2 py-1 text-xs font-bold text-white">
                VIRAL
              </div>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};
